import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, Info } from "lucide-react";
import { toast } from "sonner";
import { psDeliveryOptions, psSetDelivery } from "../../lib/psApi";
import { usePsStore } from "../../store/psStore";

export default function PsDelivery() {
  const nav = useNavigate();
  const { orderId, refresh } = usePsStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    psDeliveryOptions(orderId || undefined).then(setData).catch(() => setData({ options: [] })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (opt) => {
    if (!orderId || busy || opt.status !== "AVAILABLE") return;
    if (opt.mode === "SCHEDULED" && (opt.windows?.length ?? 0) === 0) return;
    setBusy(opt.mode);
    try {
      const body = { delivery_mode: opt.mode };
      if (opt.mode === "SCHEDULED") { const w = opt.windows[0]; body.window_start = w.start; body.window_end = w.end; }
      await psSetDelivery(orderId, body);
      await refresh();
      nav("/personal-shopper/panier");
    } catch (e) { toast.error(e?.response?.data?.detail?.message || "Ce mode n'est plus disponible."); load(); }
    finally { setBusy(null); }
  };

  const options = data?.options || [];
  const standard = options.filter((o) => o.mode.endsWith("STANDARD"));
  const premium = options.filter((o) => !o.mode.endsWith("STANDARD") && o.status === "AVAILABLE");
  const visible = (o) => o.status === "AVAILABLE" || o.status === "CLOSED";
  const next = data?.next_standard;

  const Card = (o) => {
    const dis = o.status !== "AVAILABLE";
    return (
      <button key={o.mode} onClick={() => choose(o)} disabled={dis} data-testid={`ps-delivery-${o.mode}`}
        className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 mb-2.5 text-left"
        style={{ border: "1px solid #f0f0f0", opacity: dis ? 0.55 : 1 }}>
        <div className="flex-1">
          <p className="font-extrabold" style={{ color: dis ? "#94a3b8" : "#0b1220" }}>{o.label}</p>
          <p className="text-xs mt-0.5" style={{ color: "#94a3b8" }}>{o.status === "CLOSED" ? "Fermé pour aujourd'hui" : "Tarif calculé à l'étape suivante"}</p>
        </div>
        {!dis && <ChevronRight size={20} color="#cbd5e1" />}
      </button>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => nav("/personal-shopper/panier")} data-testid="ps-delivery-back" style={{ color: "#0b1220" }}><ArrowLeft size={24} /></button>
        <h1 className="text-xl font-extrabold" style={{ color: "#0b1220" }}>Quand être livré ?</h1>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} />)}</div>
      ) : (
        <>
          <p className="text-xs font-extrabold tracking-widest mb-3" style={{ color: "#64748b" }}>LIVRAISONS STANDARD</p>
          {standard.filter(visible).map(Card)}
          {standard.every((o) => o.status !== "AVAILABLE") && next && (
            <div className="flex items-center gap-2 rounded-xl p-3.5" style={{ background: "#e0f2fe" }}>
              <Info size={18} color="#0369a1" /><span className="text-sm" style={{ color: "#075985" }}>Prochaine livraison standard : {next.wave_type === "MORNING" ? "fin de matinée" : "fin d'après-midi"} du {next.date}.</span>
            </div>
          )}

          <p className="text-xs font-extrabold tracking-widest mb-3 mt-6" style={{ color: "#64748b" }}>LIVRAISONS À LA DEMANDE</p>
          {premium.length === 0 ? <p className="text-sm" style={{ color: "#94a3b8" }}>Aucune option à la demande disponible pour le moment.</p> : premium.map(Card)}
        </>
      )}
    </div>
  );
}
